import { API } from '@/config/api';
import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar, Plus, Trash2, Bell, Check, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';


const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'eod', label: 'Every Other Day (EOD)' },
  { value: '2x_week', label: '2x Per Week' },
  { value: '3x_week', label: '3x Per Week' },
  { value: '5on_2off', label: '5 On / 2 Off' },
  { value: 'custom', label: 'Custom Days' }
];

export default function Scheduler() {
  const { user } = useOutletContext();
  const [schedules, setSchedules] = useState([]);
  const [peptides, setPeptides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    peptide_id: '',
    peptide_name: '',
    frequency: 'daily',
    times: ['08:00'],
    days: [],
    dose_amount: '',
    dose_unit: 'mcg'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [schedulesRes, peptidesRes] = await Promise.all([
        fetch(`${API}/schedules`, { credentials: 'include' }),
        fetch(`${API}/peptides`, { credentials: 'include' })
      ]);

      if (schedulesRes.ok) setSchedules(await schedulesRes.json());
      if (peptidesRes.ok) setPeptides(await peptidesRes.json());
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePeptideSelect = (peptideId) => {
    const peptide = peptides.find(p => p.peptide_id === peptideId);
    setFormData({
      ...formData,
      peptide_id: peptideId,
      peptide_name: peptide?.name || ''
    });
  };

  const handleTimeChange = (index, value) => {
    const newTimes = [...formData.times];
    newTimes[index] = value;
    setFormData({ ...formData, times: newTimes });
  };

  const addTime = () => {
    setFormData({ ...formData, times: [...formData.times, '12:00'] });
  };

  const removeTime = (index) => {
    const newTimes = formData.times.filter((_, i) => i !== index);
    setFormData({ ...formData, times: newTimes });
  };

  const toggleDay = (day) => {
    const newDays = formData.days.includes(day)
      ? formData.days.filter(d => d !== day)
      : [...formData.days, day];
    setFormData({ ...formData, days: newDays });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.peptide_id) {
      toast.error('Please select a peptide');
      return;
    }

    try {
      const response = await fetch(`${API}/schedules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          peptide_id: formData.peptide_id,
          peptide_name: formData.peptide_name,
          frequency: formData.frequency,
          times: formData.times,
          days: formData.days
        })
      });

      if (response.ok) {
        toast.success('Schedule created');
        setShowDialog(false);
        setFormData({
          peptide_id: '',
          peptide_name: '',
          frequency: 'daily',
          times: ['08:00'],
          days: [],
          dose_amount: '',
          dose_unit: 'mcg'
        });
        fetchData();
      } else {
        toast.error('Failed to create schedule');
      }
    } catch (error) {
      toast.error('Failed to create schedule');
    }
  };

  const handleComplete = async (scheduleId) => {
    try {
      const response = await fetch(`${API}/schedules/${scheduleId}/complete`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Dose logged & next dose: ${new Date(data.next_dose_date).toLocaleDateString()}`);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to mark complete');
    }
  };

  const handleDelete = async (scheduleId) => {
    if (!confirm('Delete this schedule?')) return;

    try {
      const response = await fetch(`${API}/schedules/${scheduleId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Schedule deleted');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const getTodaySchedules = () => {
    const today = new Date();
    const dayName = DAYS_OF_WEEK[today.getDay() === 0 ? 6 : today.getDay() - 1];
    
    return schedules.filter(s => {
      if (!s.is_active) return false;
      
      // Check if today matches frequency
      const freq = s.frequency?.toLowerCase() || '';
      if (freq === 'daily') return true;
      if (freq === 'custom' && s.days?.includes(dayName)) return true;
      if (freq === '5on_2off' && today.getDay() !== 0 && today.getDay() !== 6) return true;
      
      // For EOD, check next_dose_date
      if (s.next_dose_date) {
        const nextDose = new Date(s.next_dose_date);
        return nextDose.toDateString() === today.toDateString();
      }
      
      return false;
    });
  };

  const getUpcomingSchedules = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    return schedules.filter(s => {
      if (!s.is_active) return false;
      if (s.next_dose_date) {
        const nextDose = new Date(s.next_dose_date);
        return nextDose > today;
      }
      return false;
    }).slice(0, 5);
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-muted-foreground">Loading schedules...</p>
      </div>
    );
  }

  const todaySchedules = getTodaySchedules();

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto" data-testid="scheduler-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2">Dose Scheduler</h1>
          <p className="text-muted-foreground">Manage your dosing schedules and reminders</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button data-testid="add-schedule-button">
              <Plus className="h-4 w-4 mr-2" />
              New Schedule
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create Schedule</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Peptide</Label>
                <Select value={formData.peptide_id} onValueChange={handlePeptideSelect}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select peptide" />
                  </SelectTrigger>
                  <SelectContent>
                    {peptides.map(peptide => (
                      <SelectItem key={peptide.peptide_id} value={peptide.peptide_id}>
                        {peptide.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Frequency</Label>
                <Select value={formData.frequency} onValueChange={(v) => setFormData({...formData, frequency: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map(freq => (
                      <SelectItem key={freq.value} value={freq.value}>
                        {freq.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formData.frequency === 'custom' && (
                <div>
                  <Label className="mb-2 block">Days</Label>
                  <div className="flex flex-wrap gap-2">
                    {DAYS_OF_WEEK.map(day => (
                      <label key={day} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={formData.days.includes(day)}
                          onCheckedChange={() => toggleDay(day)}
                        />
                        <span className="text-sm">{day.slice(0, 3)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label className="mb-2 block">Dose Times</Label>
                {formData.times.map((time, index) => (
                  <div key={index} className="flex items-center gap-2 mb-2">
                    <Input
                      type="time"
                      value={time}
                      onChange={(e) => handleTimeChange(index, e.target.value)}
                      className="flex-1"
                    />
                    {formData.times.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeTime(index)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addTime}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Time
                </Button>
              </div>

              <Button type="submit" className="w-full">
                <Calendar className="h-4 w-4 mr-2" />
                Create Schedule
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Today's Schedule */}
      <Card className="p-6 glass-card mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Bell className="h-6 w-6 text-primary" />
          <h2 className="text-2xl font-bold">Today's Doses</h2>
        </div>

        {todaySchedules.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No doses scheduled for today</p>
        ) : (
          <div className="space-y-3">
            {todaySchedules.map(schedule => (
              <div key={schedule.schedule_id} className="flex items-center justify-between p-4 rounded-lg bg-secondary/50 border border-border">
                <div className="flex items-center gap-4">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Clock className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{schedule.peptide_name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {schedule.times?.join(', ') || 'Not set'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleComplete(schedule.schedule_id)}
                    data-testid={`complete-${schedule.schedule_id}`}
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Complete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* All Schedules */}
      <h2 className="text-2xl font-bold mb-4">All Schedules</h2>
      
      {schedules.length === 0 ? (
        <Card className="p-12 text-center glass-card">
          <Calendar className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-semibold mb-2">No Schedules Yet</h2>
          <p className="text-muted-foreground mb-6">
            Create a schedule to get reminders for your doses.
          </p>
          <Button onClick={() => setShowDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Your First Schedule
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {schedules.map(schedule => (
            <Card key={schedule.schedule_id} className="p-4 glass-card" data-testid={`schedule-${schedule.schedule_id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-lg">{schedule.peptide_name}</h3>
                  <p className="text-sm text-primary font-medium">
                    {FREQUENCIES.find(f => f.value === schedule.frequency)?.label || schedule.frequency}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {schedule.times?.map((time, i) => (
                      <span key={i} className="px-2 py-1 rounded bg-secondary text-xs">
                        {time}
                      </span>
                    ))}
                  </div>
                  {schedule.days?.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-2">
                      {schedule.days.map(d => d.slice(0, 3)).join(', ')}
                    </p>
                  )}
                  {schedule.next_dose_date && (
                    <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Next: {new Date(schedule.next_dose_date).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <span className={`px-2 py-1 rounded text-xs ${schedule.is_active ? 'bg-success/20 text-success' : 'bg-muted text-muted-foreground'}`}>
                    {schedule.is_active ? 'Active' : 'Paused'}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(schedule.schedule_id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
